import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { DTORBIT_URL } from "@/constants";

const NAV_LINKS = [
    { label: "Home", to: "/", testid: "nav-link-home" },
    { label: "About Us", to: "/about", testid: "nav-link-about" },
    { label: "Ventures", to: "/ventures", testid: "nav-link-ventures" },
    { label: "Overview", to: "/overview", testid: "nav-link-overview" },
    { label: "Leadership", to: "/leadership", testid: "nav-link-leadership" },
    { label: "Investors", to: "/investors", testid: "nav-link-investors" },
    { label: "Careers", to: "/careers", testid: "nav-link-careers" },
    { label: "Contact Us", to: "/contact", testid: "nav-link-contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled || open
                    ? "glass border-b border-[#E4EAF6]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-[72px] flex items-center gap-4">
                <Link
                    to="/"
                    data-testid="header-logo-link"
                    aria-label="DtOrbit home"
                    className="flex items-center flex-none"
                    onClick={() => setOpen(false)}
                >
                    <img
                        src="/assets/dtorbit-logo-sm.png"
                        alt="DtOrbit USDT to INR exchange platform logo"
                        className="h-6 md:h-7 w-auto block"
                        width="160"
                        height="40"
                    />
                </Link>

                <div className="flex items-center gap-8 ml-auto">
                    <nav
                        className="hidden lg:flex items-center gap-5"
                        aria-label="Primary"
                    >
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to === "/"}
                            data-testid={link.testid}
                            className={({ isActive }) =>
                                `nav-link text-sm font-semibold transition-colors ${
                                    isActive
                                        ? "text-[#1E56FF]"
                                        : "text-[#4A5A7E] hover:text-[#1E56FF]"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={DTORBIT_URL}
                        data-testid="header-start-exchange-button"
                        aria-label="Start USDT to INR Exchange on DtOrbit"
                        className="btn-primary hidden sm:inline-flex !py-2.5 !px-5 text-sm"
                    >
                        Start Exchange
                        <ArrowRight className="w-4 h-4" />
                    </a>
                    <button
                        type="button"
                        data-testid="mobile-menu-button"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl glass text-[#0F1B3D]"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    </div>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu-panel"
                    className="lg:hidden glass border-t border-[#E4EAF6] px-4 sm:px-6 py-4 flex flex-col gap-1"
                >
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to === "/"}
                            data-testid={`mobile-${link.testid}`}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `py-3 px-2 text-sm font-semibold transition-colors border-b border-[#EDF2FB] ${
                                    isActive
                                        ? "text-[#1E56FF]"
                                        : "text-[#1E2A4A] hover:text-[#1E56FF]"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                    <a
                        href={DTORBIT_URL}
                        data-testid="mobile-start-exchange-button"
                        className="btn-primary mt-3 w-full"
                    >
                        Start Exchange
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            )}
        </header>
    );
}
